import { useTranslation } from 'react-i18next';

export default function LicenseDetailsSection() {
  const { t } = useTranslation();

  const membershipFeatures = [
    { icon: 'ri-building-2-fill', key: 'official' },
    { icon: 'ri-hand-coin-fill', key: 'trading' },
    { icon: 'ri-shield-star-fill', key: 'regulated' },
    { icon: 'ri-global-fill', key: 'international' },
    { icon: 'ri-bar-chart-fill', key: 'market' },
    { icon: 'ri-group-fill', key: 'network' },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">

        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-5 py-2 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-award-fill text-amber-600 text-sm"></i>
            </div>
            <span className="text-amber-700 text-sm font-semibold tracking-widest uppercase">
              {t('license.details.badge')}
            </span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('license.details.title')}</h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">{t('license.details.subtitle')}</p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">

          {/* License Image Display */}
          <div className="flex flex-col items-center">
            <div className="relative w-full max-w-md">
              {/* Decorative frame */}
              <div className="absolute -inset-3 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-2xl opacity-20 blur-sm"></div>
              <div className="relative bg-white border-2 border-amber-200 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-amber-600 tracking-widest uppercase">
                    {t('license.image.label')}
                  </span>
                  <div className="flex gap-1">
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-600"></div>
                  </div>
                </div>

                {/* License Image Area */}
                    
                <div className="w-full bg-white rounded-xl border border-amber-100 overflow-hidden flex items-center justify-center p-2">
                  <img
                    src="/img/license.png"
                    alt={t('license.image.desc')}
                    className="w-full h-auto object-contain rounded-lg"
                  />
                </div>

                <div className="mt-4 flex items-center justify-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-verified-badge-fill text-amber-500 text-base"></i>
                  </div>
                  <span className="text-sm text-amber-700 font-medium">{t('license.image.verified')}</span>
                </div>
              </div>
            </div>

            {/* Member Number Badge */}
            <div className="mt-8 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-2xl p-6 w-full max-w-md text-center shadow-lg">
              <p className="text-amber-100 text-sm font-semibold tracking-widest uppercase mb-2">
                {t('license.member.label')}
              </p>
              <div className="text-white text-6xl font-bold mb-2">10</div>
              <p className="text-amber-100 font-medium">{t('license.member.title')}</p>
            </div>
          </div>

          {/* License Info */}
          <div>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('license.info.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('license.info.desc1')}
              </p>
              <p className="text-gray-600 leading-relaxed">
                {t('license.info.desc2')}
              </p>
            </div>

            {/* Key Info Cards */}
            <div className="grid grid-cols-1 gap-4 mb-8">
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-building-4-fill text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">
                    {t('license.info.org.label')}
                  </p>
                  <p className="text-gray-800 font-semibold">{t('license.info.org.value')}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-hashtag text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">
                    {t('license.info.member.label')}
                  </p>
                  <p className="text-gray-800 font-semibold">{t('license.info.member.value')}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-map-pin-fill text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">
                    {t('license.info.jurisdiction.label')}
                  </p>
                  <p className="text-gray-800 font-semibold">{t('license.info.jurisdiction.value')}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-checkbox-circle-fill text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">
                    {t('license.info.status.label')}
                  </p>
                  <p className="text-gray-800 font-semibold text-green-600">{t('license.info.status.value')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Membership Features */}
        <div>
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">
            {t('license.features.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {membershipFeatures.map((feat) => (
              <div
                key={feat.key}
                className="p-6 bg-white rounded-xl border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all duration-300 cursor-default group"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 group-hover:bg-amber-100 rounded-xl mb-4 transition-colors duration-300">
                  <i className={`${feat.icon} text-amber-600 text-2xl`}></i>
                </div>
                <h4 className="text-gray-900 font-semibold mb-2">{t(`license.features.${feat.key}.title`)}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{t(`license.features.${feat.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
